<template>
  <article>
    <div v-if="title" class="chart-title">
      {{ title }}
    </div>
    <div class="chart-scroll">
      <div class="chart-canvas" :style="canvasStyle">
        <bar-chart
          :chart-data="chartData"
          :chart-options="chartOptions"
          :plugins="[htmlLegendPlugin]"
          style="min-height: 440px"
        />
      </div>
    </div>
    <div class="chart-bottom">
      <div class="chart-legend-wrap">
        <div :id="legendContainerId" class="chart-legend"></div>
        <div class="legend-hint">
          <span class="legend-hint__sample legend-hint__sample--current"></span>
          выбранный период
          <span class="legend-hint__sample legend-hint__sample--prev"></span>
          прошлый период
        </div>
      </div>
      <div class="dataset-label">
        {{ datasetLabel }}
      </div>
    </div>
  </article>
</template>

<script>
  import { Bar as BarChart } from 'vue-chartjs'
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    LinearScale,
    CategoryScale,
  } from 'chart.js'

  import PeriodType from '@/models/enums/statistics/PeriodType'
  import { hexToRgba, positioningTooltip } from '@/util/chart'
  import { createTooltip } from '@/util/revenue-dynamics/tooltip'
  import { renderStackedTooltip } from '@/util/revenue-dynamics/stacked-tooltip'
  import chartColors from '@/constants/chartColors'

  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    LinearScale,
    CategoryScale,
  )

  // Штатный позиционер average усредняет координаты всех сегментов обоих стеков,
  // включая нулевые у оси, и уводит подсказку к низу графика. Ставим её у курсора.
  Tooltip.positioners.cursor = (items, eventPosition) => eventPosition || false

  // Ширина столбцов: прошлый период уже текущего на 40%. Центр слота от barPercentage
  // не зависит, поэтому узкий столбец остаётся на своём месте в паре.
  const CURRENT_BAR_PERCENTAGE = 0.8
  const PREV_BAR_PERCENTAGE = CURRENT_BAR_PERCENTAGE * 0.4

  export default {
    name: 'ConnectorDynamicsChart',
    components: { BarChart },
    props: {
      legendContainerId: String,
      items: {
        type: Array,
        default: () => [],
      },
      periodType: {
        type: String,
        default: PeriodType.Week,
      },
      title: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        htmlLegendPlugin: {
          id: 'htmlLegend',
          afterUpdate (chart, args, options) {
            const legendContainer = document.getElementById(options.containerID)

            while (legendContainer.firstChild) {
              legendContainer.firstChild.remove()
            }

            const items = chart.options.plugins.legend.labels.generateLabels(chart)

            items.forEach((item) => {
              const label = document.createElement('div')
              label.style.alignItems = 'center'
              label.style.cursor = 'pointer'
              label.style.display = 'flex'
              label.style.flexDirection = 'row'

              if (!chart.data.datasets[item.datasetIndex].showTooltip) {
                label.style.display = 'none'
              }

              const seriesKey = chart.data.datasets[item.datasetIndex].seriesKey
              const prevDatasetIndex = chart.data.datasets.findIndex(i => {
                return seriesKey === i.seriesKey && !i.showTooltip
              })

              label.onclick = () => {
                chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex))
                chart.setDatasetVisibility(prevDatasetIndex, !chart.isDatasetVisible(prevDatasetIndex))
                chart.update()
              }

              const rectangle = document.createElement('div')
              rectangle.style.background = item.fillStyle
              rectangle.style.borderColor = item.strokeStyle
              rectangle.style.borderWidth = item.lineWidth + 'px'
              rectangle.style.height = '23px'
              rectangle.style.marginRight = '5px'
              rectangle.style.borderRadius = '5px'
              rectangle.style.width = window.matchMedia('(max-width: 600px)').matches ? '70px' : '137px'

              label.appendChild(rectangle)

              const iconPath = chart.data.datasets[item.datasetIndex].icon

              if (iconPath) {
                // Красим иконку плашки в цвет серии через CSS-маску (SVG рисуется силуэтом).
                const icon = document.createElement('span')
                icon.style.display = 'inline-block'
                icon.style.marginRight = '5px'
                icon.style.width = '23px'
                icon.style.height = '23px'
                icon.style.backgroundColor = item.fillStyle
                icon.style.webkitMaskImage = `url(${iconPath})`
                icon.style.maskImage = `url(${iconPath})`
                icon.style.webkitMaskRepeat = 'no-repeat'
                icon.style.maskRepeat = 'no-repeat'
                icon.style.webkitMaskPosition = 'center'
                icon.style.maskPosition = 'center'
                icon.style.webkitMaskSize = 'contain'
                icon.style.maskSize = 'contain'
                label.appendChild(icon)
              }

              const textContainer = document.createElement('p')
              textContainer.style.color = item.fontColor
              textContainer.style.margin = 0
              textContainer.style.padding = 0
              textContainer.style.textDecoration = item.hidden ? 'line-through' : ''

              textContainer.appendChild(document.createTextNode(item.text))

              label.appendChild(textContainer)
              legendContainer.appendChild(label)
            })
          },
        },
      }
    },
    computed: {
      // Даты разных коннекторов могут не совпадать - собираем объединённый отсортированный список.
      labels () {
        const dates = new Set()

        this.items.forEach(item => {
          const points = item.items || []

          points.forEach(point => dates.add(point.date))
        })

        return Array.from(dates).sort()
      },
      // На десктопе столбцы сжимаются под ширину, на узких экранах держим минимум и скроллим вбок.
      canvasStyle () {
        if (!this.$vuetify.breakpoint.smAndDown) {
          return {}
        }

        return { minWidth: Math.max(this.labels.length * 34, 320) + 'px' }
      },
      series () {
        return this.items.map((item, index) => {
          const points = item.items || []
          const pointsByDate = {}

          points.forEach(point => {
            pointsByDate[point.date] = point
          })

          return {
            seriesKey: `${item.connectorId}_${item.tileKey}`,
            label: item.connectorName || item.name,
            icon: item.icon,
            color: chartColors[index % chartColors.length],
            pointsByDate,
          }
        })
      },
      // Дата сравнения для каждой даты - берём из первой серии, где эта точка есть.
      prevDateByDate () {
        const map = {}

        this.items.forEach(item => {
          const points = item.items || []

          points.forEach(point => {
            if (!map[point.date]) {
              map[point.date] = point.prevDate
            }
          })
        })

        return map
      },
      chartOptions () {
        return {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          scales: {
            x: {
              stacked: true,
              grid: { display: true, drawOnChartArea: false },
              ticks: {
                callback: (value, index) => this.formatDateLabel(this.labels[index]),
              },
            },
            y: {
              stacked: true,
              beginAtZero: true,
            },
          },
          plugins: {
            htmlLegend: {
              containerID: this.legendContainerId,
            },
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'cursor',
              external: this.externalTooltipHandler,
            },
          },
        }
      },
      chartData () {
        const datasets = []

        this.series.forEach(series => {
          datasets.push({
            label: series.label,
            backgroundColor: series.color,
            borderWidth: 0,
            data: this.labels.map(date => (series.pointsByDate[date] ? series.pointsByDate[date].value : 0)),
            barPercentage: CURRENT_BAR_PERCENTAGE,
            stack: 'current',
            showTooltip: true,
            seriesKey: series.seriesKey,
            icon: series.icon,
          })

          datasets.push({
            label: series.label,
            backgroundColor: hexToRgba(series.color, 0.7),
            borderWidth: 0,
            data: this.labels.map(date => (series.pointsByDate[date] ? series.pointsByDate[date].prevValue : 0)),
            barPercentage: PREV_BAR_PERCENTAGE,
            stack: 'prev',
            showTooltip: false,
            seriesKey: series.seriesKey,
            icon: series.icon,
          })
        })

        return { labels: this.labels, datasets }
      },
      datasetLabel () {
        if (!this.labels.length) {
          return ''
        }

        const format = { day: 'numeric', month: 'numeric', year: 'numeric' }
        const startDate = new Date(this.labels[0]).toLocaleDateString('ru', format)

        if (this.labels.length === 1) {
          return startDate
        }

        const endDate = new Date(this.labels[this.labels.length - 1]).toLocaleDateString('ru', format)

        return startDate + ' - ' + endDate
      },
    },
    methods: {
      externalTooltipHandler (context) {
        const { chart, tooltip } = context
        const tooltipEl = createTooltip(chart)

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        if (!tooltip.dataPoints || !tooltip.dataPoints.length) {
          return
        }

        while (tooltipEl.firstChild) {
          tooltipEl.firstChild.remove()
        }

        const date = this.labels[tooltip.dataPoints[0].dataIndex]
        const rows = []
        let total = 0
        let prevTotal = 0

        this.series.forEach(series => {
          const datasetIndex = chart.data.datasets.findIndex(
            dataset => dataset.seriesKey === series.seriesKey && dataset.showTooltip,
          )

          // Серия скрыта через легенду - не показываем её ни строкой, ни в итогах.
          if (datasetIndex === -1 || !chart.isDatasetVisible(datasetIndex)) {
            return
          }

          const point = series.pointsByDate[date]

          if (!point || (!point.value && !point.prevValue)) {
            return
          }

          total += point.value
          prevTotal += point.prevValue

          rows.push({
            label: series.label,
            color: series.color,
            icon: series.icon,
            value: point.value,
            prevValue: point.prevValue,
          })
        })

        if (!rows.length) {
          tooltipEl.style.opacity = 0
          return
        }

        renderStackedTooltip(tooltipEl, {
          periodType: this.periodType,
          dates: { date, prevDate: this.prevDateByDate[date] },
          rows,
          total,
          prevTotal,
        })

        positioningTooltip(chart, tooltip, tooltipEl)

        // Курсор может стоять у нижнего края - подсказка не должна вываливаться под график.
        const maxTop = chart.canvas.offsetTop + chart.height - tooltipEl.offsetHeight
        const top = parseFloat(tooltipEl.style.top)

        if (top > maxTop) {
          tooltipEl.style.top = Math.max(chart.canvas.offsetTop, maxTop) + 'px'
        }
      },
      formatDateLabel (date) {
        if (!date) {
          return ''
        }

        const parsed = new Date(date)

        return this.periodType === PeriodType.Year
          ? parsed.toLocaleDateString('ru', { month: 'short', year: 'numeric' })
          : parsed.toLocaleDateString('ru', { day: 'numeric', month: 'short' })
      },
    },
  }
</script>

<style lang="less" scoped>
.chart-title {
  font-size: 20px;
  margin-bottom: 30px;
}
.chart-scroll {
  // overflow-x переводит и overflow-y из visible в auto, из-за чего абсолютная подсказка
  // рождает вертикальный скролл. Поэтому включаем скролл только там, где он нужен.
  @media (max-width: 959px) {
    overflow-x: auto;
  }
}
// Подсказка позиционируется от канваса: без relative она считает координаты
// от внешнего предка и уезжает при горизонтальном скролле.
.chart-canvas {
  position: relative;
}
.chart-legend-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.legend-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #7E7E7E;
  margin-top: 5px;

  &__sample {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 3px;
    margin-right: 5px;

    &--current {
      background: #7E7E7E;
    }

    &--prev {
      background: rgba(126, 126, 126, 0.45);
      margin-left: 15px;
    }
  }
}
// Сетка вместо потока: при разной длине названий колонки остаются выровненными по левому краю.
.chart-legend {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  align-items: center;
  column-gap: 30px;
  row-gap: 10px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
}
.chart-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;

  @media (max-width: 960px) {
    flex-direction: column-reverse;
  }

  @media (max-width: 500px) {
    align-items: flex-start;
  }
}
.dataset-label {
  font-size: 14px;
  color: #000;

  @media (max-width: 960px) {
    margin-bottom: 15px;
  }
}
</style>
